"use client";

import { Share2 } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getAbsoluteUrl } from "@/lib/routes";

export interface CopyToClipboardButtonProps
  extends React.ComponentProps<typeof Button> {
  /** The text to copy to clipboard */
  textToCopy: string;
  /** Optional callback when copy is successful */
  onCopy?: () => void;
  /** Custom tooltip text for hover state */
  hoverTooltip?: string;
  /** Custom tooltip text for success state */
  successTooltip?: string;
  /** Custom test id */
  testId?: string;
  /** Icon to display (only used for icon variant) */
  icon?: React.ReactNode;
  /** Button variant - 'icon' for icon-only button, 'text' for text button */
  buttonVariant?: "icon" | "text";
  /** Text to display (only used for text variant) */
  children?: React.ReactNode;
}

export default function CopyToClipboardButton({
  textToCopy,
  onCopy,
  hoverTooltip = "Copy to clipboard",
  successTooltip = "Copied to clipboard!",
  testId,
  variant = "outline",
  size = "sm",
  className,
  icon = <Share2 className="w-4 h-4" />,
  buttonVariant = "icon",
  children,
}: CopyToClipboardButtonProps) {
  const [tooltipMessage, setTooltipMessage] = useState(hoverTooltip);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const resetTimeoutRef = useRef<NodeJS.Timeout>(undefined);

  useEffect(() => () => clearTimeout(resetTimeoutRef.current), []);

  const handleCopyClick = async () => {
    const absoluteUrl = getAbsoluteUrl(textToCopy);
    await navigator.clipboard.writeText(absoluteUrl);
    onCopy?.();

    // Show success tooltip for 2 seconds
    setTooltipMessage(successTooltip);
    setIsTooltipOpen(true);
    clearTimeout(resetTimeoutRef.current);
    resetTimeoutRef.current = setTimeout(() => {
      setTooltipMessage(hoverTooltip);
      setIsTooltipOpen(false);
    }, 2000);
  };

  // Set default className based on variant
  const defaultClassName =
    buttonVariant === "icon"
      ? "rounded-md h-10 w-10 p-0"
      : "w-1/2 sm:w-auto flex items-center justify-center gap-2 border-line-strong text-ink-emphasis hover:bg-surface-subtle py-2 px-4";

  const finalClassName = className || defaultClassName;

  return (
    <TooltipProvider>
      <Tooltip open={isTooltipOpen} onOpenChange={setIsTooltipOpen}>
        <TooltipTrigger asChild>
          <Button
            variant={variant}
            size={size}
            className={finalClassName}
            onClick={handleCopyClick}
            data-testid={testId}
            aria-label="Copy share link to clipboard"
          >
            {buttonVariant === "icon" ? (
              icon
            ) : (
              <>
                {icon}
                {children}
              </>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipMessage}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
